import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Select,
  VStack,
} from '@chakra-ui/react';

import DateField from 'components/fields/DateField';
import MultiSelect from 'components/fields/MultiSelect';
import SectionCard from '../../components/SectionCard';
import ValidatedField from '../../components/ValidatedField';
import { useAnswerCodes, useValidationSchema } from '../../hooks';
import type { VehicleComponentProps } from './types';
import {
  formatDateToString,
  parseDateString,
} from 'components/fields/auto/resolvers/resolverUtils';

const VehiclePurchaseInfo = ({
  vehicle,
  index,
  onVehicleChange,
}: VehicleComponentProps) => {
  const { getOptions, getLabel, answerCodes, isLoading } = useAnswerCodes();
  const { isFieldRequired } = useValidationSchema();

  const vehicleEntity = vehicle as unknown as Record<string, unknown>;
  const isRequired = (field: string) =>
    isFieldRequired('vehicle', field, vehicleEntity);

  const ownershipLabel = getLabel(
    vehicle.vehicle_ownership,
    'vehicle_ownership',
  ).toLowerCase();
  const isLeased = ownershipLabel === 'leased';
  const isFinanced = ownershipLabel === 'financed';

  const cardTitle = isLeased
    ? 'Lease Information'
    : isFinanced
      ? 'Finance Information'
      : 'Purchase Information';
  const dateLabel = isLeased
    ? 'Lease Date'
    : isFinanced
      ? 'Finance Date'
      : 'Purchase Date';
  const kmLabel = isLeased ? 'KM at Lease' : 'KM at Purchase';
  const conditionOptions = getOptions('condition_when_purchased_leased');
  const deviceTypeOptions = getOptions('antitheft_device_type');
  const manufacturerOptions = getOptions('antitheft_product');
  const safetyFeaturesOptions = answerCodes?.vehicle_safety_features ?? [];

  const antiTheftCodes =
    answerCodes?.conditional_rules?.antitheft_device_type?.show_for_codes ?? [];
  const safetyFeaturesOtherCodes =
    answerCodes?.conditional_rules?.vehicle_safety_features_other
      ?.show_for_codes ?? [];
  const deviceTypeOtherCodes =
    answerCodes?.conditional_rules?.antitheft_device_type_other
      ?.show_for_codes ?? [];

  const winterTiresCode = safetyFeaturesOptions.find((opt) =>
    opt.label.toLowerCase().includes('winter tire'),
  )?.code;

  const safetyFeaturesItems = safetyFeaturesOptions.map((opt) => ({
    id: parseInt(opt.code, 10),
    name: opt.label,
  }));

  const deviceTypeItems = deviceTypeOptions
    .filter((opt) => opt.value)
    .map((opt) => ({
      id: parseInt(opt.value, 10),
      name: opt.label,
    }));

  const manufacturerItems = manufacturerOptions
    .filter((opt) => opt.value)
    .map((opt) => ({
      id: parseInt(opt.value, 10),
      name: opt.label,
    }));

  const selectedSafetyFeatureIds = (vehicle.safety_features || []).map((code) =>
    parseInt(code, 10),
  );

  const selectedDeviceTypeIds = (vehicle.anti_theft.device_types || []).map(
    (code) => parseInt(code, 10),
  );

  const selectedManufacturerIds = (vehicle.anti_theft.manufacturers || []).map(
    (code) => parseInt(code, 10),
  );

  const handleSafetyFeaturesChange = (ids: number[]) => {
    const codes = ids.map((id) => id.toString());
    onVehicleChange(index, 'safety_features', codes);
  };

  const handleWinterTiresChange = (checked: boolean) => {
    if (!winterTiresCode) return;

    const currentFeatures = vehicle.safety_features || [];
    const hasCode = currentFeatures.includes(winterTiresCode);

    if (checked && !hasCode) {
      onVehicleChange(index, 'safety_features', [
        ...currentFeatures,
        winterTiresCode,
      ]);
    } else if (!checked && hasCode) {
      onVehicleChange(
        index,
        'safety_features',
        currentFeatures.filter((code) => code !== winterTiresCode),
      );
    }
  };

  const handleDeviceTypesChange = (ids: number[]) => {
    const codes = ids.map((id) => id.toString());
    onVehicleChange(index, 'anti_theft', codes, 'device_types');
  };

  const handleManufacturersChange = (ids: number[]) => {
    const codes = ids.map((id) => id.toString());
    onVehicleChange(index, 'anti_theft', codes, 'manufacturers');
  };

  const safetyFeatures = vehicle.safety_features || [];
  const deviceTypes = vehicle.anti_theft.device_types || [];
  const isWinterTiresChecked = winterTiresCode
    ? safetyFeatures.includes(winterTiresCode)
    : vehicle.discounts.winter_tires;
  const hasAntiTheftSelected = antiTheftCodes.some((code) =>
    safetyFeatures.includes(code),
  );
  const showSafetyFeaturesOther = safetyFeaturesOtherCodes.some((code) =>
    safetyFeatures.includes(code),
  );
  const showDeviceTypeOther = deviceTypeOtherCodes.some((code) =>
    deviceTypes.includes(code),
  );

  return (
    <Grid
      templateColumns={{
        base: '1fr',
        md: 'repeat(2, 1fr)',
        lg: 'repeat(3, 1fr)',
      }}
      gap={4}
      mb={4}
    >
      <GridItem>
        <SectionCard title={cardTitle} h="100%">
          <FormControl
            mb={3}
            isRequired={isRequired('purchase_info.condition')}
            isInvalid={
              isRequired('purchase_info.condition') &&
              !vehicle.purchase_info.condition
            }
          >
            <FormLabel fontSize="xs">Condition</FormLabel>
            <Select
              variant="main"
              size="sm"
              value={vehicle.purchase_info.condition || ''}
              onChange={(e) =>
                onVehicleChange(
                  index,
                  'purchase_info',
                  e.target.value,
                  'condition',
                )
              }
              isDisabled={false}
            >
              {conditionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl
            isRequired={isRequired('purchase_info.purchase_date')}
            isInvalid={
              isRequired('purchase_info.purchase_date') &&
              !vehicle.purchase_info.purchase_date
            }
          >
            <DateField
              label={dateLabel}
              date={
                vehicle.purchase_info.purchase_date
                  ? parseDateString(vehicle.purchase_info.purchase_date)
                  : null
              }
              onChange={(val: Date | null) => {
                onVehicleChange(
                  index,
                  'purchase_info',
                  formatDateToString(val),
                  'purchase_date',
                );
              }}
              isDisabled={false}
              required={
                isRequired('purchase_info.purchase_date') &&
                !vehicle.purchase_info.purchase_date
              }
            />
          </FormControl>
          <Box mt={2}>
            <ValidatedField
              itemPath="vehicle"
              fieldPath="purchase_info.km_at_purchase"
              label={kmLabel}
              entity={vehicleEntity}
              labelSize="xs"
            >
              <Input
                variant="main"
                size="sm"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={vehicle.purchase_info.km_at_purchase ?? ''}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '');
                  onVehicleChange(
                    index,
                    'purchase_info',
                    digits ? Number(digits) : null,
                    'km_at_purchase',
                  );
                }}
                isDisabled={false}
              />
            </ValidatedField>
          </Box>
        </SectionCard>
      </GridItem>

      {/* Safety Features & Anti-Theft */}
      <GridItem>
        <SectionCard title="Safety Features" h="100%">
          <FormControl mb={3}>
            <MultiSelect
              selectedIds={selectedSafetyFeatureIds}
              availableItems={safetyFeaturesItems}
              isLoading={isLoading}
              onChange={handleSafetyFeaturesChange}
              label="Select safety features"
              itemLabelSingular="feature"
              itemLabelPlural="features"
              isDisabled={false}
              showTags={false}
            />
          </FormControl>

          {showSafetyFeaturesOther && (
            <FormControl mb={3}>
              <FormLabel fontSize="xs">Other (please specify)</FormLabel>
              <Input
                variant="main"
                size="sm"
                value={vehicle.safety_features_other || ''}
                onChange={(e) =>
                  onVehicleChange(
                    index,
                    'safety_features_other',
                    e.target.value,
                  )
                }
                isDisabled={false}
                placeholder="Describe safety feature..."
              />
            </FormControl>
          )}

          {hasAntiTheftSelected && (
            <Box pt={3} borderTopWidth="1px">
              <FormControl mb={2}>
                <FormLabel fontSize="xs">Device Types</FormLabel>
                <MultiSelect
                  selectedIds={selectedDeviceTypeIds}
                  availableItems={deviceTypeItems}
                  isLoading={isLoading}
                  onChange={handleDeviceTypesChange}
                  label="Select device types"
                  itemLabelSingular="device"
                  itemLabelPlural="devices"
                  isDisabled={false}
                  showTags={false}
                />
              </FormControl>
              {showDeviceTypeOther && (
                <FormControl mb={2}>
                  <FormLabel fontSize="xs">Device Type Other</FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    value={vehicle.anti_theft.device_type_other || ''}
                    onChange={(e) =>
                      onVehicleChange(
                        index,
                        'anti_theft',
                        e.target.value,
                        'device_type_other',
                      )
                    }
                    isDisabled={false}
                    placeholder="Describe device type..."
                  />
                </FormControl>
              )}
              <FormControl>
                <FormLabel fontSize="xs">Manufacturers</FormLabel>
                <MultiSelect
                  selectedIds={selectedManufacturerIds}
                  availableItems={manufacturerItems}
                  isLoading={isLoading}
                  onChange={handleManufacturersChange}
                  label="Select manufacturers"
                  itemLabelSingular="manufacturer"
                  itemLabelPlural="manufacturers"
                  isDisabled={false}
                  showTags={false}
                />
              </FormControl>
            </Box>
          )}
        </SectionCard>
      </GridItem>

      {/* Discounts */}
      <GridItem>
        <SectionCard title="Discounts" h="100%">
          <VStack align="start" spacing={2}>
            <Checkbox
              isChecked={vehicle.discounts.single_vehicle_mvd}
              onChange={(e) =>
                onVehicleChange(
                  index,
                  'discounts',
                  e.target.checked,
                  'single_vehicle_mvd',
                )
              }
              isDisabled={false}
            >
              Single Vehicle MVD
            </Checkbox>
            <Checkbox
              isChecked={isWinterTiresChecked}
              onChange={(e) => handleWinterTiresChange(e.target.checked)}
              isDisabled={false}
            >
              Winter Tires
            </Checkbox>
          </VStack>
        </SectionCard>
      </GridItem>
    </Grid>
  );
};

export default VehiclePurchaseInfo;
